<template>
  <div
    :class="{ 'forward-mode': isForwardMode }"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <div class="flex flex-row gap-4 p-2 border-b border-gray-100">
      <div
        v-if="isForwardMode"
        class="flex items-center gap-1 text-sm font-medium ltr:pl-2 ltr:pr-[29px] rtl:pr-2 rtl:pl-[29px] py-[3px] bg-transparent border-0 cursor-default"
      >
        <span
          class="material-symbols-outlined text-gray-500 text-base/[16px] scale-x-[-100%]"
        >
          reply
        </span>
        <span class="text-xs text-gray-600">
          {{ $t('CONVERSATION.REPLYBOX.FORWARD') }}
        </span>
      </div>
      <div v-else v-on-clickaway="closeDropdown" class="reply-mode-select">
        <button
          ref="modeTrigger"
          class="mode-trigger"
          :class="{ active: showDropdownReplyType }"
          @click.stop="toggleDropdown"
        >
          <span
            class="material-symbols-outlined text-gray-500 text-base/[16px]"
          >
            {{ selectedMode.icon }}
          </span>
          <span class="text-xs text-gray-600 whitespace-nowrap">
            {{ selectedMode.label }}
          </span>
          <span
            class="material-symbols-outlined text-gray-500 text-base/[16px]"
          >
            keyboard_arrow_down
          </span>
        </button>
        <div
          v-if="replyModeOptions.length && showDropdownReplyType"
          ref="modeDropdown"
          class="mode-dropdown"
          :style="modeDropdownStyle"
        >
          <div
            v-for="option in replyModeOptions"
            :key="option.value"
            class="mode-option ltr:text-left rtl:text-right"
            :class="{ 'is-active': option.value === mode }"
            @click="onModeChange(option)"
          >
            <span
              class="material-symbols-outlined text-gray-500 text-[20px]/[20px]"
              :class="{
                'scale-x-[-100%]': option.value === FORWARD_VALUE_CONSTANT,
              }"
            >
              {{ option.icon }}
            </span>
            <span class="flex-1 text-sm">{{ option.label }}</span>
            <span
              v-if="option.value === mode"
              class="material-symbols-outlined text-[#164B35] text-[20px]/[20px]"
            >
              check
            </span>
          </div>
        </div>
      </div>
      <div
        class="flex flex-1"
        :class="{
          'justify-between': !isPrivateNoteMode && fromEmailVal,
          'justify-end': !fromEmailVal.length || isPrivateNoteMode,
        }"
      >
        <div
          v-if="!isPrivateNoteMode && fromEmailVal"
          class="flex items-center gap-1 py-[3px]"
        >
          <span class="text-xs text-gray-900 capitalize min-w-[40px]">
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.LABEL') }}: &nbsp;
          </span>
          <span
            v-if="
              fromEmailVal && !$v.fromEmailVal.$error && !isEditingFromEmail
            "
            class="text-xs semantic-tag tag-mute-01 flex items-center gap-1"
          >
            <span>{{ fromEmailVal }}</span>
            <span
              v-if="false"
              class="material-symbols-outlined flex items-center justify-center text-sm w-4 h-4 rounded-[4px] hover:bg-gray-100 hover:cursor-pointer"
              @click="removeFromEmailVal"
            >
              close
            </span>
          </span>
          <div v-else :class="{ error: $v.fromEmailVal.$error }">
            <div class="search-from-input relative">
              <contact-input-search
                email-mode
                skip-selected
                :email="$v.fromEmailVal.$model"
                field="email"
                :placeholder="
                  $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.PLACEHOLDER')
                "
                @on-select-contact="onSelect('fromEmailVal', $event)"
                @input="$v.fromEmailVal.$model = $event"
                @focus="isEditingFromEmail = true"
                @blur="onBlur"
              />
              <span
                v-if="$v.fromEmailVal.$error"
                v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.ERROR')"
                class="material-symbols-outlined text-base/[16px] font-[500] text-[#EF4444] absolute top-[50%] ltr:right-0 rtl:left-0 -translate-y-1/2 cursor-default"
              >
                info
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <template v-if="!isPrivateNoteMode">
            <div
              class="flex items-center p-[7px] button-ghost"
              :class="{ 'bg-gray-100 border-gray-100': showCc }"
              @click.stop="toggleCCfield"
            >
              <span class="material-symbols-outlined text-[16px] text-gray-500">
                add
              </span>
              <span class="text-gray-600 text-xs">
                {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL') }}
              </span>
            </div>
            <div
              class="flex items-center p-[7px] button-ghost"
              :class="{ 'bg-gray-100 border-gray-100': showBcc }"
              @click.stop="toggleBCCField"
            >
              <span class="material-symbols-outlined text-[16px] text-gray-500">
                add
              </span>
              <span class="text-gray-600 text-xs">
                {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL') }}
              </span>
            </div>
          </template>
          <div
            v-if="!isForwardMode"
            class="flex items-center p-2 hover:bg-gray-100 rounded-[4px] cursor-pointer"
            @click.stop="onCloseReplyBox"
          >
            <span class="material-symbols-outlined text-[16px] text-gray-500">
              delete
            </span>
          </div>
        </div>
      </div>
    </div>
    <template v-if="!isPrivateNoteMode">
      <!-- TO -->
      <div class="input-group-wrap">
        <div
          class="flex items-center gap-1 py-[3px]"
          :class="{ error: $v.toEmailsVal.$error }"
        >
          <span class="text-xs text-gray-900 capitalize min-w-[40px]">
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.TO') }}:
          </span>
          <template v-if="sendToMultiple">
            <div class="input-group-field">
              <contact-input-search
                email-mode
                multiple
                skip-selected
                :email="$v.toEmailsVal.$model"
                field="email"
                :placeholder="
                  $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.PLACEHOLDER')
                "
                @on-select-contact="onSelect('toEmailsVal', $event)"
                @input="$v.toEmailsVal.$model = $event"
                @remove-tag="removeTag('toEmailsVal', $event)"
                @blur="onBlur"
              />
            </div>
          </template>
          <template v-else>
            <span
              v-if="toEmailsVal && !$v.toEmailsVal.$error && !isEditingToEmail"
              class="text-xs semantic-tag tag-mute-01 flex items-center gap-1"
            >
              <span>{{ toEmailsVal }}</span>
              <span
                class="material-symbols-outlined flex items-center justify-center text-sm w-4 h-4 rounded-[4px] hover:bg-gray-100 hover:cursor-pointer"
                @click="removeToEmailVal"
              >
                close
              </span>
            </span>
            <div v-else :class="{ error: $v.toEmailsVal.$error }">
              <div class="search-from-input relative py-[5px]">
                <contact-input-search
                  email-mode
                  skip-selected
                  :email="$v.toEmailsVal.$model"
                  field="email"
                  :placeholder="
                    $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.PLACEHOLDER')
                  "
                  @on-select-contact="onSelect('toEmailsVal', $event)"
                  @input="$v.toEmailsVal.$model = $event"
                  @focus="isEditingToEmail = true"
                  @blur="onBlur"
                />
                <span
                  v-if="$v.toEmailsVal.$error"
                  v-tooltip.top="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.FROM.ERROR')"
                  class="material-symbols-outlined text-base/[16px] font-[500] text-[#EF4444] absolute top-[50%] ltr:right-0 rtl:left-0 -translate-y-1/2 cursor-default"
                >
                  info
                </span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- CC -->
      <div v-if="showCc" class="input-group-wrap">
        <div
          class="input-group small items-center"
          :class="{ error: $v.ccEmailsVal.$error }"
        >
          <label class="input-group-label">
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.LABEL') }}:
          </label>
          <div class="input-group-field">
            <contact-input-search
              email-mode
              multiple
              skip-selected
              :email="$v.ccEmailsVal.$model"
              field="email"
              :placeholder="$t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.PLACEHOLDER')"
              @on-select-contact="onSelect('ccEmailsVal', $event)"
              @input="$v.ccEmailsVal.$model = $event"
              @remove-tag="removeTag('ccEmailsVal', $event)"
              @blur="onBlur"
            />
          </div>
          <div
            class="flex items-center p-2 hover:bg-gray-100 !rounded-[4px] cursor-pointer"
            @click="toggleCCfield"
          >
            <span class="material-symbols-outlined text-[16px] text-gray-500">
              close
            </span>
          </div>
        </div>
        <span v-if="$v.ccEmailsVal.$error" class="message">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.CC.ERROR') }}
        </span>
      </div>
      <!-- BCC -->
      <div v-if="showBcc" class="input-group-wrap">
        <div
          class="input-group small items-center"
          :class="{ error: $v.bccEmailsVal.$error }"
        >
          <label class="input-group-label">
            {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.LABEL') }}:
          </label>
          <div class="input-group-field">
            <contact-input-search
              email-mode
              multiple
              :email="$v.bccEmailsVal.$model"
              field="email"
              :placeholder="
                $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.PLACEHOLDER')
              "
              skip-selected
              @on-select-contact="onSelect('bccEmailsVal', $event)"
              @input="$v.bccEmailsVal.$model = $event"
              @remove-tag="removeTag('bccEmailsVal', $event)"
              @blur="onBlur"
            />
          </div>
          <div
            class="flex items-center p-2 hover:bg-gray-100 !rounded-[4px] cursor-pointer"
            @click="toggleBCCField"
          >
            <span class="material-symbols-outlined text-[16px] text-gray-500">
              close
            </span>
          </div>
        </div>
        <span v-if="$v.bccEmailsVal.$error" class="message">
          {{ $t('CONVERSATION.REPLYBOX.EMAIL_HEAD.BCC.ERROR') }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';
import { validEmailsByComma } from 'dashboard/components/widgets/conversation/helpers/emailHeadHelper.js';
import ContactInputSearch from 'dashboard/modules/contact/components/ContactInputSearchV2.vue';
import emailValidator from 'vuelidate/lib/validators/email';
import {
  hasPressedAltAndPKey,
  hasPressedAltAndLKey,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { mixin as clickaway } from 'vue-clickaway';
import rtlMixin from 'shared/mixins/rtlMixin';
import { getFixedDialogStyle } from 'dashboard/helper/dialogPositionHelper';

export default {
  components: {
    ContactInputSearch,
  },
  mixins: [eventListenerMixins, hasPermissionMixin, clickaway, rtlMixin],
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    setReplyMode: {
      type: Function,
      default: () => {},
    },
    ccEmails: {
      type: String,
      default: '',
    },
    bccEmails: {
      type: String,
      default: '',
    },
    fwdEmails: {
      type: String,
      default: '',
    },
    fromEmail: {
      type: String,
      default: '',
    },
    toEmails: {
      type: String,
      default: '',
    },
    isShowingReplyBox: {
      type: Boolean,
      required: true,
    },
    sendToMultiple: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDropdownReplyType: false,
      dropdownStyle: {},
      showCc: this.mode === REPLY_EDITOR_MODES.REPLY_ALL,
      showBcc: false,
      isForwardMode: this.mode === REPLY_EDITOR_MODES.FORWARD,
      ccEmailsVal: '',
      bccEmailsVal: '',
      fwdEmailsVal: '',
      fromEmailVal: '',
      toEmailsVal: '',
      isEditingFromEmail: false,
      isEditingToEmail: false,
      FORWARD_VALUE_CONSTANT: REPLY_EDITOR_MODES.FORWARD,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
    }),
    replyModeOptions() {
      let options = [];
      if (this.hasInboxPermission('Inbox reply')) {
        options.push(
          {
            value: REPLY_EDITOR_MODES.REPLY,
            label: this.$t('CONVERSATION.REPLYBOX.REPLY'),
            icon: 'reply',
          },
          {
            value: REPLY_EDITOR_MODES.REPLY_ALL,
            label: this.$t('CONVERSATION.REPLYBOX.REPLY_ALL'),
            icon: 'reply_all',
          }
        );
      }
      if (this.hasInboxPermission('Inbox new message')) {
        options.push({
          value: REPLY_EDITOR_MODES.FORWARD,
          label: this.$t('CONVERSATION.REPLYBOX.FORWARD'),
          icon: 'reply',
        });
      }
      if (this.hasInboxPermission('Inbox private notes')) {
        options.push({
          value: REPLY_EDITOR_MODES.NOTE,
          label: this.$t('CONVERSATION.REPLYBOX.NOTE'),
          icon: 'draft',
        });
      }
      return options;
    },
    selectedMode() {
      return this.replyModeOptions.find(opt => opt.value === this.mode);
    },
    isReplyMode() {
      return this.mode === REPLY_EDITOR_MODES.REPLY;
    },
    isReplyAllMode() {
      return this.mode === REPLY_EDITOR_MODES.REPLY_ALL;
    },
    isPrivateNoteMode() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },
    modeDropdownStyle() {
      return this.dropdownStyle;
    },
  },
  watch: {
    bccEmails(newVal) {
      if (newVal !== this.bccEmailsVal) {
        this.bccEmailsVal = newVal;
      }
    },
    // d99d
    fwdEmails(newVal) {
      if (newVal !== this.fwdEmailsVal) {
        this.fwdEmailsVal = newVal;
      }
    },
    ccEmails(newVal) {
      if (newVal !== this.ccEmailsVal) {
        this.ccEmailsVal = newVal;
      }
    },
    fromEmail(newVal) {
      if (newVal !== this.fromEmailVal) {
        this.fromEmailVal = newVal;
      }
    },
    toEmails(newVal) {
      if (newVal !== this.toEmailsVal) {
        this.toEmailsVal = newVal;
      }
    },
    mode(val) {
      if (val === REPLY_EDITOR_MODES.REPLY_ALL) {
        this.showCc = true;
        this.ccEmailsVal = this.ccEmails;
      }
    },
    showCc(val) {
      if (!val) {
        this.ccEmailsVal = '';
      }
    },
    showBcc(val) {
      if (!val) {
        this.bccEmailsVal = '';
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScrollRecalculate, true);
  },
  mounted() {
    if (this.isReplyAllMode) {
      this.ccEmailsVal = this.ccEmails;
      this.bccEmailsVal = this.bccEmails;
      this.$nextTick(() => {
        this.showBcc = !!this.bccEmails.length;
      });
    }
    this.fwdEmailsVal = this.fwdEmails;
    this.fromEmailVal = this.fromEmail;
    this.toEmailsVal = this.toEmails;
  },
  validations: {
    ccEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    bccEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    fwdEmailsVal: {
      hasValidEmails(value) {
        return validEmailsByComma(value);
      },
    },
    fromEmailVal: {
      hasValidEmails(value) {
        return emailValidator(value);
      },
    },
    toEmailsVal: {
      hasValidEmails(value) {
        if (this.sendToMultiple) return validEmailsByComma(value);
        return emailValidator(value);
      },
    },
  },
  methods: {
    onSelect(field, contact) {
      const { email } = contact;
      if (this.isForwardMode && field === 'toEmailsVal') {
        this.$emit('set-forward-contact', contact);
      }
      const oldVal = this.$v[field].$model
        ? this.$v[field].$model.replaceAll(',', ' ').split(' ')
        : [];
      const lastVal = oldVal.slice(-1)[0];
      if (lastVal && !validEmailsByComma(lastVal)) oldVal.pop();
      let newVal = [oldVal.filter(val => val), email.replaceAll(',', ' ')]
        .flat()
        .filter(val => val)
        .concat([' '])
        .join(', ');
      const singleEmailField = this.sendToMultiple
        ? ['fromEmailVal']
        : ['fromEmailVal', 'toEmailsVal'];
      if (singleEmailField.includes(field)) newVal = newVal.replace(/, *$/, '');
      this.$v[field].$model = newVal;
      this.onBlur();
    },
    toggleCCfield() {
      this.ccEmailsVal = '';
      this.$emit('update:ccEmails', this.ccEmailsVal);
      this.showCc = !this.showCc;
    },
    toggleBCCField() {
      this.bccEmailsVal = '';
      this.$emit('update:bccEmails', this.bccEmailsVal);
      this.showBcc = !this.showBcc;
    },
    onBlur() {
      this.isEditingFromEmail = false;
      this.isEditingToEmail = false;
      this.$v.$touch();
      this.$emit('update:bccEmails', this.bccEmailsVal);
      this.$emit('update:fwdEmails', this.fwdEmailsVal);
      this.$emit('update:ccEmails', this.ccEmailsVal);
      this.$emit('update:toEmails', this.toEmailsVal);
      this.$emit('update:fromEmail', this.fromEmailVal);
    },
    closeDropdown() {
      this.showDropdownReplyType = false;
      window.removeEventListener('scroll', this.onScrollRecalculate, true);
    },
    calculateDropdownPosition() {
      const triggerRect = this.$refs.modeDropdown.getBoundingClientRect();
      this.dropdownStyle = {
        ...getFixedDialogStyle(this.$refs.modeTrigger, {
          width: triggerRect.width,
          height: triggerRect.height,
          gap: 4,
          align: this.isRTLView ? 'right' : 'left',
          preferredPlacement: 'bottom',
        }),
        zIndex: 9999,
      };
    },
    onScrollRecalculate() {
      if (this.showDropdownReplyType) this.calculateDropdownPosition();
    },
    toggleDropdown() {
      this.showDropdownReplyType = !this.showDropdownReplyType;
      if (this.showDropdownReplyType) {
        this.$nextTick(() => {
          this.calculateDropdownPosition();
        });
        window.addEventListener('scroll', this.onScrollRecalculate, true);
      } else {
        window.removeEventListener('scroll', this.onScrollRecalculate, true);
      }
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndPKey(e)) {
        this.handleNoteClick();
      }
      if (hasPressedAltAndLKey(e)) {
        this.handleReplyClick();
      }
    },
    onModeChange(option) {
      this.setReplyMode(option.value);
      this.closeDropdown();
      if (option.value === this.FORWARD_VALUE_CONSTANT) {
        this.onCloseReplyBox();
      }
    },
    handleReplyClick() {
      this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
    },
    handleNoteClick() {
      this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
    },
    onCloseReplyBox() {
      this.$emit('dismiss');
      this.$emit('update:isShowingReplyBox', false);
    },
    removeTag(field, email) {
      const existing = this.$v[field].$model
        .split(',')
        .map(s => s.trim())
        .filter(e => e && e !== email);
      this.$v[field].$model = existing.join(', ');
      this.onBlur();
    },
    removeFromEmailVal() {
      this.fromEmailVal = null;
      this.$emit('update:fromEmail', null);
    },
    removeToEmailVal() {
      this.toEmailsVal = null;
      this.$emit('update:toEmails', null);
    },
  },
};
</script>
<style lang="scss" scoped>
.forward-mode {
  &[dir='rtl'] {
    .input-group-wrap {
      @apply ltr:pl-[108px] ltr:pr-2 rtl:pr-[108px] rtl:pl-2;
    }
  }
  &[dir='ltr'] {
    .input-group-wrap {
      @apply ltr:pl-[128px] ltr:pr-2 rtl:pr-[128px] rtl:pl-2;
    }
  }
}
.input-group-wrap {
  @apply ltr:pl-[144px] ltr:pr-2 rtl:pr-[144px] rtl:pl-2 py-1 border-b border-gray-100;

  .message {
    @apply text-xs text-red-500 dark:text-red-500;
  }
}

.input-group {
  margin-bottom: 0;
  gap: 4px;
  .input-group-label {
    @apply min-w-[40px] border-transparent bg-transparent text-xs text-gray-900 px-0 font-normal;
  }
  .input-group-field {
    @apply flex flex-row items-center w-full;
    &::v-deep input {
      @apply mb-0 border-transparent w-[300px];
    }
  }
}

.input-group.error {
  @apply border-b-red-500 dark:border-b-red-500;
  .input-group-label {
    @apply text-red-500 dark:text-red-500;
  }
}

.reply-mode-select {
  @apply relative flex items-center w-[120px];

  .mode-trigger {
    @apply flex items-center gap-1 text-sm font-medium px-2 py-[3px] cursor-pointer bg-transparent border-0;
    @apply text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white;
    @apply h-full hover:bg-gray-100 rounded-[4px];

    &.active {
      @apply bg-gray-100;
    }
  }

  .mode-dropdown {
    @apply fixed z-50 min-w-[150px] overflow-hidden;
    @apply bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-lg shadow-md;

    .mode-option {
      @apply flex flex-row items-center gap-1;
      @apply w-full text-base px-3 py-[7px] cursor-pointer bg-transparent;
      @apply text-gray-600 hover:bg-green-50 hover:text-[#164B35];

      &.is-active {
        @apply bg-green-50 text-[#164B35];
      }
    }
  }
}

::v-deep {
  .search-from-input input {
    @apply text-xs border-0 py-0 px-2 h-[26px] w-[300px];
  }
}
</style>
